import { TFunction } from 'i18next';

import { ErrorExplainerContent } from './problemDetails.types';

const NAMESPACE = 'errors';
const GENERIC_CODE = 'generic';

/**
 * Checks whether the `errors` namespace has content for a given code.
 * @param t - The translation function.
 * @param code - The error code to check.
 * @returns `true` when a localized title exists for the code.
 */
function hasContentForCode(t: TFunction, code: string): boolean {
  return t(`${code}.title`, { ns: NAMESPACE, defaultValue: '' }) !== '';
}

/**
 * Resolves localized explainer content for an error code, falling back to the
 * generic entry when the code is unknown or missing.
 * @param t - The translation function from `useTranslation`.
 * @param code - The error code (e.g. "user-already-exists"), if known.
 * @returns Localized title, description, and steps to resolve.
 */
export function getErrorContent(t: TFunction, code: string | undefined): ErrorExplainerContent {
  const resolvedCode = code && hasContentForCode(t, code) ? code : GENERIC_CODE;

  const rawSteps: unknown = t(`${resolvedCode}.steps`, {
    ns: NAMESPACE,
    returnObjects: true,
    defaultValue: [],
  });
  const steps = Array.isArray(rawSteps)
    ? rawSteps.filter((step): step is string => typeof step === 'string')
    : [];

  const knowledgeBaseUrl = t(`${resolvedCode}.knowledgeBaseUrl`, {
    ns: NAMESPACE,
    defaultValue: '',
  });

  return {
    code: resolvedCode,
    title: t(`${resolvedCode}.title`, { ns: NAMESPACE }),
    description: t(`${resolvedCode}.description`, { ns: NAMESPACE }),
    steps,
    ...(knowledgeBaseUrl !== '' ? { knowledgeBaseUrl } : {}),
  };
}
